<template>
  <div class="sms-text-box-editor-wrapper">
    <label class="floating-label">{{ label }}</label>
    <quill-editor
      ref="quillEditor"
      v-model="internalValue"
      :options="editorOptions"
      class="sms-text-box-editor"
      :class="error ? 'danger' : ''"
      @blur="handleBlur"
      @change="handleChange"
    />
  </div>
</template>

<script>
import { quillEditor } from 'vue-quill-editor';
// eslint-disable-next-line import/no-extraneous-dependencies
import 'quill/dist/quill.core.css';
// eslint-disable-next-line import/no-extraneous-dependencies
import 'quill/dist/quill.snow.css';
// eslint-disable-next-line import/no-extraneous-dependencies
import Quill from 'quill';

// Custom Blot for placeholders that behave as atomic units
const Embed = Quill.import('blots/embed');

class PlaceholderBlot extends Embed {
  static create(value) {
    const node = super.create();
    node.setAttribute('contenteditable', false);
    node.setAttribute('data-shorthand', value.shorthand);
    node.setAttribute('data-name', value.name);
    node.textContent = `[${value.name}]`;
    node.classList.add('placeholder');
    // Important: Set cursor style to text for proper interaction
    node.style.cursor = 'text';
    return node;
  }

  static value(node) {
    return {
      shorthand: node.getAttribute('data-shorthand'),
      name: node.getAttribute('data-name'),
    };
  }

  // Override length to ensure proper cursor positioning
  length() {
    return 1;
  }
}

PlaceholderBlot.blotName = 'placeholder';
PlaceholderBlot.tagName = 'span';
PlaceholderBlot.className = 'placeholder';

Quill.register(PlaceholderBlot);

export default {
  name: 'SMSTextBoxEditor',
  components: {
    quillEditor,
  },
  props: {
    value: String,
    error: {
      type: Boolean,
      required: false,
      default: false,
    },
    label: {
      type: String,
      required: true,
    },
    interpolations: {
      type: Array,
      required: false,
      default() {
        return [];
      },
      validator(interpolations) {
        return Array.isArray(interpolations) && interpolations.every((interpolation) => typeof interpolation === 'object'
          && interpolation !== null
          && 'shorthand' in interpolation
          && typeof interpolation.shorthand === 'string'
          && 'attribute' in interpolation
          && typeof interpolation.attribute === 'string');
      },
    },
  },
  data() {
    return {
      internalValue: '',
      isDirectInsertion: false,
      cursorPosition: 0, // Track last cursor position when editor loses focus
      editorOptions: {
        modules: {
          toolbar: false, // No toolbar for SMS
          keyboard: {
            bindings: {
              // Prevent line breaks in SMS
              enter: {
                key: 13,
                handler: () => true, // Do nothing on enter
              },
            },
          },
        },
        placeholder: '',
        theme: 'snow',
      },
    };
  },
  watch: {
    value: {
      immediate: true,
      handler(newVal, oldVal) {
        // Skip if this is a direct insertion (already handled)
        if (this.isDirectInsertion) {
          this.isDirectInsertion = false;
          return;
        }

        const currentPlainText = this.extractPlainText();
        if (newVal !== currentPlainText) {
          // Calculate cursor position based on content change
          const quillEditor = this.$refs.quillEditor;
          const quill = quillEditor && quillEditor.quill;
          let targetCursorPos = null;

          if (quill && oldVal !== undefined) {
            const currentSelection = quill.getSelection();
            // Only calculate cursor position if there's an active selection
            // (meaning user was typing or programmatic insertion happened)
            if (currentSelection) {
              const lengthDiff = (newVal || '').length - (oldVal || '').length;
              if (lengthDiff > 0) {
                // Text was inserted, move cursor forward
                targetCursorPos = currentSelection.index + lengthDiff;
              }
            }
          }

          this.setEditorContent(newVal, targetCursorPos);
        }
      },
    },
  },
  mounted() {
    if (this.value) {
      this.setEditorContent(this.value);
    }

    // Set up Quill selection-change listener to track cursor position
    this.$nextTick(() => {
      const quillEditor = this.$refs.quillEditor;
      const quill = quillEditor && quillEditor.quill;
      if (quill) {
        quill.on('selection-change', (range) => {
          if (range) {
            this.cursorPosition = range.index;
            console.log('selection-change - cursorPosition updated to:', range.index);
          }
        });
      }
    });
  },
  methods: {
    setEditorContent(plainText, targetCursorPos = null) {
      const quill = this.$refs.quillEditor && this.$refs.quillEditor.quill;
      if (!quill) return;

      // Disable text change event temporarily to avoid recursion
      quill.off('text-change');

      // Build delta operations for the content
      const delta = [];
      let remainingText = plainText || '';
      let quillCursorPosition = 0;
      let lastWasPlaceholder = false;

      while (remainingText.length > 0) {
        // Find the earliest placeholder in remaining text
        let earliestIndex = -1;
        let earliestInterpolation = null;

        this.interpolations.forEach((interpolation) => {
          const index = remainingText.indexOf(interpolation.shorthand);
          if (index !== -1 && (earliestIndex === -1 || index < earliestIndex)) {
            earliestIndex = index;
            earliestInterpolation = interpolation;
          }
        });

        if (earliestIndex === -1) {
          // No more placeholders, insert remaining text
          if (remainingText) {
            delta.push({ insert: remainingText });
            quillCursorPosition += remainingText.length;
          }
          break;
        } else {
          // Insert text before placeholder
          if (earliestIndex > 0) {
            delta.push({ insert: remainingText.substring(0, earliestIndex) });
            quillCursorPosition += earliestIndex;
            lastWasPlaceholder = false;
          } else if (lastWasPlaceholder && earliestIndex === 0) {
            // Two adjacent placeholders - insert a space between them
            delta.push({ insert: ' ' });
            quillCursorPosition += 1;
            lastWasPlaceholder = false;
          }

          // Insert placeholder as embed
          delta.push({
            insert: {
              placeholder: {
                shorthand: earliestInterpolation.shorthand,
                name: earliestInterpolation.name,
              },
            },
          });
          quillCursorPosition += 1; // Embeds count as 1 character in Quill
          lastWasPlaceholder = true;

          // Update remaining text
          remainingText = remainingText.substring(earliestIndex + earliestInterpolation.shorthand.length);
        }
      }

      // Set content using delta
      quill.setContents(delta);

      // Set cursor position
      this.$nextTick(() => {
        // Only set cursor position if explicitly provided (programmatic insertion)
        if (targetCursorPos !== null && targetCursorPos !== undefined) {
          // Calculate Quill cursor position from plain text position
          // We need to count how many placeholders are before the target position
          let quillPos = 0;
          let plainPos = 0;
          const text = plainText || '';

          while (plainPos < targetCursorPos && plainPos < text.length) {
            // Check if there's a placeholder at this position
            let foundPlaceholder = false;

            for (const interpolation of this.interpolations) {
              if (text.substring(plainPos).startsWith(interpolation.shorthand)) {
                // Found a placeholder, it counts as 1 in Quill
                quillPos += 1;
                plainPos += interpolation.shorthand.length;
                foundPlaceholder = true;
                break;
              }
            }

            if (!foundPlaceholder) {
              // Regular character
              quillPos += 1;
              plainPos += 1;
            }
          }

          // Only focus and set selection for programmatic insertions
          quill.focus();
          quill.setSelection(quillPos, 0);
        }
        // Don't set cursor position if user is clicking/interacting
        // Let Quill handle natural cursor placement

        // Re-enable text change event
        quill.on('text-change', () => {
          this.handleChange();
        });
      });
    },
    extractPlainText() {
      const quillEditor = this.$refs.quillEditor;
      const quill = quillEditor && quillEditor.quill;
      if (!quill) return '';

      const delta = quill.getContents();
      let plainText = '';

      delta.ops.forEach((op) => {
        if (typeof op.insert === 'string') {
          plainText += op.insert;
        } else if (op.insert && op.insert.placeholder) {
          plainText += op.insert.placeholder.shorthand;
        }
      });

      // Remove zero-width spaces (used for cursor positioning on mobile)
      plainText = plainText.replace(/\u200B/g, '');

      // Don't trim - preserve spaces at start and end
      // Remove only the trailing newline that Quill adds
      return plainText.replace(/\n$/, '');
    },
    handleChange() {
      const plainText = this.extractPlainText();
      this.$emit('input', plainText);
    },
    handleBlur() {
      const quillEditor = this.$refs.quillEditor;
      const quill = quillEditor && quillEditor.quill;
      if (!quill) return;

      const selection = quill.getSelection();
      const cursorPosition = selection ? selection.index : 0;

      // Only update cursor position if we have a valid selection
      // Don't overwrite the saved position from selection-change with 0
      if (selection) {
        this.cursorPosition = cursorPosition;
      }

      this.$emit('blur', cursorPosition);
    },
    getCurrentCursorPosition() {
      const quillEditor = this.$refs.quillEditor;
      const quill = quillEditor && quillEditor.quill;
      if (!quill) return 0;

      const selection = quill.getSelection();
      if (!selection) return this.extractPlainText().length;

      // Convert Quill cursor position to plain text position
      const delta = quill.getContents();
      let plainTextPos = 0;
      let quillPos = 0;

      for (const op of delta.ops) {
        if (quillPos >= selection.index) break;

        if (typeof op.insert === 'string') {
          const opLength = op.insert.length;
          if (quillPos + opLength > selection.index) {
            // Cursor is within this text op
            plainTextPos += selection.index - quillPos;
            break;
          }
          plainTextPos += opLength;
          quillPos += opLength;
        } else if (op.insert && op.insert.placeholder) {
          // Placeholder counts as shorthand length in plain text
          plainTextPos += op.insert.placeholder.shorthand.length;
          quillPos += 1; // But only 1 in Quill
        }
      }

      return plainTextPos;
    },
    insertPlaceholderAtCursor(shorthand, name) {
      console.log('insertPlaceholderAtCursor', shorthand, name);

      const quillEditor = this.$refs.quillEditor;
      const quill = quillEditor && quillEditor.quill;
      if (!quill) return;

      // Try to get current cursor position, otherwise use saved position from last blur
      const selection = quill.getSelection();
      const cursorIndex = selection ? selection.index : this.cursorPosition;

      console.log('insertPlaceholderAtCursor - selection:', selection, 'cursorIndex:', cursorIndex, 'saved cursorPosition:', this.cursorPosition);

      // Mark as direct insertion to skip value watcher
      this.isDirectInsertion = true;

      // Disable text change temporarily
      quill.off('text-change');

      // Insert the placeholder embed at cursor position
      quill.insertEmbed(cursorIndex, 'placeholder', {
        shorthand,
        name,
      });

      // Insert zero-width space + regular space after placeholder
      // Zero-width space FIRST: acts as persistent anchor for cursor positioning on mobile
      // Regular space AFTER: for convenience (user can delete it if needed)
      quill.insertText(cursorIndex + 1, '\u200B ');

      // Re-enable text change event
      quill.on('text-change', () => {
        this.handleChange();
      });

      // Manually trigger change to update parent
      this.handleChange();

      // Move cursor after the placeholder and spaces (placeholder=1, zwsp+space=2)
      this.positionCursorAndScroll(quill, cursorIndex + 3);
    },
    insertTextAtCursor(text) {
      const quillEditor = this.$refs.quillEditor;
      const quill = quillEditor && quillEditor.quill;
      if (!quill) return;

      // Try to get current cursor position, otherwise use saved position from last blur
      const selection = quill.getSelection();
      const cursorIndex = selection ? selection.index : this.cursorPosition;

      // Mark as direct insertion to skip value watcher
      this.isDirectInsertion = true;

      // Disable text change temporarily
      quill.off('text-change');

      // Insert text at cursor position
      quill.insertText(cursorIndex, text);

      // Insert zero-width space + regular space after text (same as placeholders)
      // Zero-width space FIRST: acts as persistent anchor for cursor positioning on mobile
      // Regular space AFTER: for convenience (user can delete it if needed)
      quill.insertText(cursorIndex + text.length, '\u200B ');

      // Re-enable text change event
      quill.on('text-change', () => {
        this.handleChange();
      });

      // Manually trigger change to update parent
      this.handleChange();

      // Move cursor after the text and spaces
      this.positionCursorAndScroll(quill, cursorIndex + text.length + 2);
    },
    positionCursorAndScroll(quill, targetIndex) {
      // Shared logic for cursor positioning and mobile scroll handling
      this.$nextTick(() => {
        quill.setSelection(targetIndex, 0);
        quill.focus();

        // Scroll cursor into view on mobile
        const editorElement = quill.root;
        const selection = quill.getSelection();
        if (selection) {
          const bounds = quill.getBounds(selection.index);
          if (bounds) {
            // Scroll to ensure cursor is visible
            const scrollTop = bounds.top - editorElement.offsetHeight / 2;
            editorElement.scrollTop = Math.max(0, scrollTop);
          }
        }
      });
    },
  },
};
</script>

<style lang="scss">
.sms-text-box-editor-wrapper {
  position: relative;
  width: 100%;

  .floating-label {
    position: absolute;
    top: -10px;
    left: 10px;
    background: white;
    padding: 0 5px;
    font-size: 12px;
    color: gray;
    pointer-events: none;
    z-index: 1;
  }

  .sms-text-box-editor {
    // Hide the toolbar completely for SMS
    .ql-toolbar {
      display: none;
    }

    .ql-container {
      border: 1px solid rgba(0, 0, 0, 0.2);
      border-radius: 6px;
      min-height: 100px;
      font-size: inherit;
    }

    .ql-editor {
      min-height: 100px;
      padding: 10px;
      line-height: 1.6;

      &.ql-blank::before {
        font-style: normal;
        color: #999;
      }
    }

    &.danger {
      .ql-container {
        border-color: rgba(var(--vs-danger), 1);
      }
    }

    // Placeholder styling
    .placeholder {
      font-weight: 600;
      color: rgba(var(--vs-primary), 1);
      cursor: text !important;
      user-select: all;
      white-space: nowrap;
      display: inline-block;

      &::selection {
        background-color: rgba(var(--vs-primary), 0.2);
      }
    }
  }
}
</style>
